/**
 * /store/success — polls /store/checkout/status/:id until paid, then
 * fires confetti and redirects to dashboard.
 */
import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import confetti from "canvas-confetti";

import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const POLL_MS = 1500;
const MAX_TRIES = 8;

export default function StoreSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const navigate = useNavigate();
  const { refreshMe } = useAuth();
  const [state, setState] = useState({ status: "checking", error: null });

  useEffect(() => {
    if (!sessionId) {
      setState({ status: "error", error: "Missing session id" });
      return;
    }
    let cancelled = false;
    let tries = 0;
    const poll = async () => {
      tries += 1;
      try {
        const { data } = await api.get(`/store/checkout/status/${sessionId}`);
        if (cancelled) return;
        if (data.payment_status === "paid") {
          confetti({ particleCount: 120, spread: 80, origin: { x: 0.5, y: 0.6 },
                     colors: ["#39FF14", "#10b981", "#fbbf24"] });
          await refreshMe?.();
          setState({ status: "paid", error: null });
          setTimeout(() => navigate("/dashboard", { replace: true }), 4000);
          return;
        }
        if (tries >= MAX_TRIES) {
          setState({ status: "timeout", error: "Still processing — check back in a minute." });
          return;
        }
        setTimeout(poll, POLL_MS);
      } catch (e) {
        if (cancelled) return;
        setState({ status: "error", error: e?.response?.data?.detail || "Failed" });
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId, navigate, refreshMe]);

  return (
    <div className="min-h-screen bg-[var(--bg)] text-[var(--text)] flex items-center justify-center px-6 text-center">
      <div data-testid="store-success-page" className="max-w-md">
        {state.status === "checking" && (
          <>
            <div className="text-xs uppercase tracking-[0.4em] text-zinc-500 mb-3">Confirming payment…</div>
            <div className="text-2xl font-bold">Almost there.</div>
          </>
        )}
        {state.status === "paid" && (
          <>
            <div className="text-7xl mb-4">💚</div>
            <h1 className="text-3xl font-black text-emerald-300">Thank you for supporting FootGuesser!</h1>
            <p className="mt-3 text-zinc-400">You&apos;re now a Donator. Redirecting to dashboard…</p>
          </>
        )}
        {(state.status === "timeout" || state.status === "error") && (
          <>
            <div className="text-xs uppercase tracking-[0.4em] text-rose-300 mb-3">Hmm</div>
            <p className="text-zinc-300">{state.error}</p>
            <Link to="/dashboard" className="inline-block mt-4 px-5 py-2 rounded-full border border-white/15 text-sm">Back to dashboard</Link>
          </>
        )}
      </div>
    </div>
  );
}
